import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Lock, Unlock } from 'lucide-react'

import type { PriceLockUnlockResponse } from '@cattle/contracts'

import { useSessionState } from '../app/session'
import { post } from '../lib/apiClient'
import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

interface PriceLockValue {
  locked: boolean
  secondsLeft: number
  /** Sends the PIN. Rejects with the server's own error if it is wrong. */
  unlock: (pin: string) => Promise<void>
  lock: () => void
  /**
   * The gate every screen puts a figure through.
   *
   * **It is a pass-through, and that is the point.** Under Price Lock the server
   * omits the `...Paisa` key entirely (§4.16) — there is nothing in the page to
   * uncover, so there is nothing here to hide. Masking client-side would mean
   * the figure had already been sent to a browser somebody else is looking over,
   * which is precisely what the feature exists to prevent. The function survives
   * because it is the one place a reader can check that claim.
   */
  gate: (paisa: number | undefined) => number | undefined
  /** Whether the tenant has Price Lock switched on at all. */
  enabled: boolean
  pinSet: boolean
  /** How long an unlock lasts, from `/me`. The PIN sheet says it out loud. */
  autoLockSeconds: number
}

const PriceLockContext = createContext<PriceLockValue | null>(null)

/**
 * Price Lock (D4), driven by the server.
 *
 * `enabled` is the tenant setting, `unlocked` is this session's state right now
 * and `unlockedUntil` is when it lapses. The client re-locks its own UI at that
 * instant so the chip does not lie; the server re-locks regardless, and every
 * subsequent payload simply arrives without its money.
 */
export function PriceLockProvider({ children }: { children: React.ReactNode }) {
  const { me, refresh } = useSessionState()
  const queryClient = useQueryClient()
  const priceLock = me?.priceLock

  const [until, setUntil] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())

  // The session payload is the authority on whether this session is unlocked;
  // the local clock only decides when to stop saying so.
  useEffect(() => {
    if (!priceLock) return
    setUntil(
      priceLock.unlocked && priceLock.unlockedUntil ? Date.parse(priceLock.unlockedUntil) : null,
    )
  }, [priceLock])

  useEffect(() => {
    if (until === null) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [until])

  const enabled = priceLock?.enabled ?? false
  const secondsLeft = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000))
  const locked = enabled && secondsLeft <= 0

  const unlock = useCallback(
    async (pin: string) => {
      const result = await post<PriceLockUnlockResponse>('/price-lock/unlock', { pin })
      setNow(Date.now())
      setUntil(result.unlockedUntil ? Date.parse(result.unlockedUntil) : null)
      // Everything cached was cached WITHOUT its money keys. Refetch, or the
      // screen stays masked behind an unlocked session.
      await Promise.all([refresh(), queryClient.invalidateQueries()])
    },
    [queryClient, refresh],
  )

  const lock = useCallback(() => {
    setUntil(null)
    void post('/price-lock/lock').finally(() => {
      // Re-fetch so the money leaves the client too, rather than lingering in a
      // cache the next render could read.
      void queryClient.invalidateQueries()
      void refresh()
    })
  }, [queryClient, refresh])

  const value = useMemo<PriceLockValue>(
    () => ({
      locked,
      secondsLeft,
      unlock,
      lock,
      gate: (paisa) => paisa,
      enabled,
      pinSet: priceLock?.pinSet ?? false,
      autoLockSeconds: priceLock?.autoLockSeconds ?? 0,
    }),
    [locked, secondsLeft, unlock, lock, enabled, priceLock?.pinSet, priceLock?.autoLockSeconds],
  )

  return <PriceLockContext.Provider value={value}>{children}</PriceLockContext.Provider>
}

export function usePriceLock(): PriceLockValue {
  const value = useContext(PriceLockContext)
  if (value) return value
  // The platform console has no Price Lock of its own — nothing to gate.
  return {
    locked: false,
    secondsLeft: 0,
    unlock: async () => {},
    lock: () => {},
    gate: (p) => p,
    enabled: false,
    pinSet: false,
    autoLockSeconds: 0,
  }
}

function mmss(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/** The chip in the page header that says whether prices are showing, and why. */
export function LockChip({ onRequestUnlock }: { onRequestUnlock?: () => void }) {
  const { locked, secondsLeft, lock, enabled } = usePriceLock()

  // Off for this book: there is no state to report and no button to press.
  if (!enabled) return null

  return (
    <button
      type="button"
      onClick={() => (locked ? onRequestUnlock?.() : lock())}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-bold whitespace-nowrap',
        'gl transition-colors',
        locked ? 'text-muted' : 'text-accent-dark',
      )}
    >
      <span className="z flex items-center gap-1.5">
        {locked ? <Lock className="size-3.5" /> : <Unlock className="size-3.5" />}
        {locked ? t('money.locked') : t('money.visibleFor', { time: mmss(secondsLeft) })}
      </span>
    </button>
  )
}
